import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthenticationService from "../../Api/AuthenticationService";
import { AuthContext } from "../../Context/AuthContext";

const SignInLayer = () => {
  const AuthObject = new AuthenticationService();
  const { rememberCredentials, getRememberedCredentials } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    mobile_number: "",
    password: "",
    remember: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Load remembered credentials on component mount
  useEffect(() => {
    const remembered = getRememberedCredentials();
    if (remembered.number && remembered.password) {
      setFormData(prev => ({
        ...prev,
        mobile_number: String(remembered.number),
        password: remembered.password,
        remember: true
      }));
    }
  }, [getRememberedCredentials]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : String(value)
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate mobile number
    const phoneRegex = /^\d{10,12}$/;
    const mobileNumber = String(formData.mobile_number).trim();
    if (!mobileNumber) {
      newErrors.mobile_number = "Mobile number is required";
    } else if (!phoneRegex.test(mobileNumber)) {
      newErrors.mobile_number = "Invalid mobile number format";
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        // Handle remember me functionality
        if (formData.remember) {
          rememberCredentials(formData.mobile_number, formData.password);
        }

        const response = await AuthObject.signin({
          ...formData,
          mobile_number: String(formData.mobile_number).trim()
        });
        const data = await response.data;

        if (response.status === 200 && data.status === "success") {
          // Navigate to OTP verification with the token
          navigate('/verify-otp', {
            state: {
              token: data.data.token,
              mobile_number: formData.mobile_number
            }
          });
        } else {
          // Handle API errors
          setErrors(prev => ({
            ...prev,
            submit: data.message || "Login failed. Please try again."
          }));
        }
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          submit: "Something went wrong. Please try again."
        }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <div>
        <Link to="/" className="mb-40 max-w-290-px">
          <img src="assets/images/logo.png" alt="" />
        </Link>
        <h4 className="mb-12">Sign In to your Account</h4>
        <p className="mb-32 text-secondary-light text-lg">
          Welcome back! please enter your detail
        </p>
      </div>
      {errors.submit && (
        <div className="alert alert-danger mb-3" role="alert">
          {errors.submit}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="icon-field mb-16">
          <span className="icon top-50 translate-middle-y">
            <Icon icon="solar:phone-outline" />
          </span>
          <input
            type="tel"
            className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.mobile_number ? 'is-invalid' : ''}`}
            placeholder="Mobile Number"
            name="mobile_number"
            value={formData.mobile_number}
            onChange={handleChange}
          />
          {errors.mobile_number && <div className="invalid-feedback">{errors.mobile_number}</div>}
        </div>

        <div className="position-relative mb-20">
          <div className="icon-field">
            <span className="icon top-50 translate-middle-y">
              <Icon icon="solar:lock-password-outline" />
            </span>
            <input
              type={showPassword ? "text" : "password"}
              className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.password ? 'is-invalid' : ''}`}
              placeholder="Password"
              name="password"
              value={formData.password}
              onChange={handleChange}
            />
            {errors.password && <div className="invalid-feedback">{errors.password}</div>}
          </div>
          <span
            className="toggle-password cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
            onClick={() => setShowPassword(!showPassword)}
          >
            <Icon icon={showPassword ? "solar:eye-closed-outline" : "solar:eye-outline"} />
          </span>
        </div>

        <div className="">
          <div className="d-flex justify-content-between gap-2">
            <div className="form-check style-check d-flex align-items-center">
              <input
                className="form-check-input border border-neutral-300"
                type="checkbox"
                id="remember"
                name="remember"
                checked={formData.remember}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="remember">
                Remember me
              </label>
            </div>
            <Link to="/forgot-password" className="text-primary-600 fw-medium">
              Forgot Password?
            </Link>
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          ) : null}
          Sign In
        </button>

        <div className="mt-32 center-border-horizontal text-center">
          <span className="bg-base z-1 px-4">Or sign in with</span>
        </div>

       

        <div className="mt-32 text-center text-sm">
          <p className="mb-0">
            Don't have an account?{" "}
            <Link to="/sign-up" className="text-primary-600 fw-semibold">
              Sign Up
            </Link>
          </p>
        </div>
      </form>
    </>
  );
};

export default SignInLayer;
